/**
 * Client-side photo resize for inline profile photos (issue #478).
 *
 * The contract with the API (`api/src/lib/profilePhoto.ts`): a
 * `data:image/jpeg|png|webp;base64,...` string of at most 40000 characters.
 * This module exists to make an honest phone camera photo (multi-MB JPEG)
 * fit that contract — a 256px square WebP is 10–20KB binary, ~27K chars as
 * base64. The SERVER enforces the cap and the allowlist; this resizer is
 * the convenience that makes real uploads pass it, never the security
 * boundary.
 *
 * Cover-crop to a square: profile photos render in circular avatars
 * everywhere (AccountMenu, CoachProfileView), and a squashed aspect ratio
 * in a circle reads as a mistake.
 */

/** Mirrors the API cap (`PROFILE_PHOTO_MAX_LENGTH`) and the DB CHECK. */
export const PROFILE_PHOTO_MAX_LENGTH = 40000;

/** "Small" per Martin's word on #459 — the stored edge size in pixels. */
export const PROFILE_PHOTO_SIZE = 256;

/**
 * Resize an image File to a 256px square data: URL.
 *
 * WebP first (the #294 option-B format); if this browser's canvas encoder
 * doesn't do WebP (`toDataURL` silently falls back to PNG — older Safari),
 * re-encode as JPEG rather than shipping the PNG: a photographic 256px PNG
 * routinely exceeds the 40000-char cap that WebP and JPEG sit far under.
 *
 * Throws on anything that is not a decodable image — the caller owns the
 * toast.
 */
export async function resizePhotoToDataUrl(file) {
  const image = await loadImage(file);
  const size = PROFILE_PHOTO_SIZE;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");

  // Cover-crop: scale the shorter edge to `size`, centre the longer one.
  const scale = Math.max(size / image.width, size / image.height);
  const w = image.width * scale;
  const h = image.height * scale;
  ctx.drawImage(image, (size - w) / 2, (size - h) / 2, w, h);

  const webp = canvas.toDataURL("image/webp", 0.8);
  if (webp.startsWith("data:image/webp")) return webp;
  return canvas.toDataURL("image/jpeg", 0.85);
}

/**
 * Decode via an object URL + `<img>`, not `createImageBitmap` — same
 * result, wider support, and the `<img>` path honours EXIF orientation in
 * every current browser (a sideways headshot is the classic phone-upload
 * bug). The object URL is revoked on both outcomes.
 */
function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("That file couldn't be read as an image."));
    };
    img.src = url;
  });
}
